import type { OTCDealData } from '@src/types/OTC';
import { logger, NotificationVariants, type OTCProductTemplateSecurity } from '@talos/kyoko';
import type { ValidateDealSecurityResult } from './validateInstantiatedDealSecurityForModify';

export type AddDealModifyToast = (options: { text: string; variant: NotificationVariants }) => void;

const PRODUCT_TEMPLATE_NOT_FOUND_TOAST =
  'Cannot modify deal: product template not found. The deal may have been created with a template that is no longer available.';

/** Logs, toasts, and returns false when {@link validateInstantiatedDealSecurityForModify} failed. */
export function notifyIfDealSecurityInvalidForModify(
  deal: Pick<OTCDealData, 'ID' | 'Symbol'>,
  dealSecurity: ValidateDealSecurityResult,
  addToast: AddDealModifyToast
): dealSecurity is Extract<ValidateDealSecurityResult, { ok: true }> {
  if (!dealSecurity.ok) {
    logger.warn('Cannot modify deal: deal instrument not in reference data or wrong type', {
      extra: { dealId: deal.ID, symbol: deal.Symbol, message: dealSecurity.message },
    });
    addToast({
      text: dealSecurity.message,
      variant: NotificationVariants.Negative,
    });
    return false;
  }
  return true;
}

/** Logs, toasts, and returns false when the deal has no product template for modify. */
export function notifyIfProductTemplateMissingForModify(
  deal: Pick<OTCDealData, 'ID' | 'TemplateSymbol'>,
  productTemplate: OTCProductTemplateSecurity | undefined,
  addToast: AddDealModifyToast
): productTemplate is OTCProductTemplateSecurity {
  if (!productTemplate) {
    logger.warn('Cannot modify deal: product template not found', {
      extra: { dealId: deal.ID, templateSymbol: deal.TemplateSymbol },
    });
    addToast({
      text: PRODUCT_TEMPLATE_NOT_FOUND_TOAST,
      variant: NotificationVariants.Negative,
    });
    return false;
  }
  return true;
}
